import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  SvgIconProps,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip
} from '@mui/material';
import { 
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';

interface FormSectionProps {
  title: string;
  icon: React.ReactElement<SvgIconProps>;
  tooltip?: string;
  children: React.ReactNode;
  sx?: any;
  isRequired?: boolean;
  defaultExpanded?: boolean;
}

const FormSection: React.FC<FormSectionProps> = ({ 
  title,
  icon,
  tooltip,
  children,
  sx = {},
  isRequired = false,
  defaultExpanded = false
}) => {
  // Use the defaultExpanded prop to control initial state
  const [expanded, setExpanded] = useState(defaultExpanded);

  const handleChange = (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded);
  };

  return (
    <Accordion 
      expanded={expanded}
      onChange={handleChange}
      sx={{ 
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        '&:before': { display: 'none' },
        boxShadow: 'none',
        mb: 3,
        ...sx
      }}
    >
      <AccordionSummary 
        expandIcon={<ExpandMoreIcon />}
        sx={{ 
          borderRadius: expanded ? '8px 8px 0 0' : 2,
          '& .MuiAccordionSummary-content': { 
            alignItems: 'center',
            gap: 2,
            py: 1
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {icon}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
          {tooltip && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {tooltip}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip 
            label={isRequired ? 'Required' : 'Optional'} 
            size="small" 
            variant="filled"
            sx={{ 
              ml: 1,
              fontSize: '0.75rem',
              height: '24px',
              backgroundColor: isRequired ? '#dc2626' : '#6b7280',
              color: 'white',
              fontWeight: 600
            }}
          />
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 3 }}>
        {children}
      </AccordionDetails>
    </Accordion>
  );
};

export default FormSection; 